(() => {
  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: currency || 'EUR',
        minimumFractionDigits: 2,
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency || 'EUR'}`;
    }
  };

  const initialize = (page) => {
    if (page.dataset.initialized === 'true') return;
    page.dataset.initialized = 'true';

    const countdown = page.querySelector('[data-ab-countdown]');
    if (countdown) {
      const duration = Math.max(1, Number(countdown.dataset.hours || 4)) * 3600000;
      const key = 'aurevona-sale-countdown';
      let end = Number(localStorage.getItem(key));
      if (!end || end <= Date.now()) {
        end = Date.now() + duration;
        localStorage.setItem(key, String(end));
      }
      const tick = () => {
        const remaining = Math.max(0, end - Date.now());
        const values = [
          Math.floor(remaining / 3600000),
          Math.floor((remaining % 3600000) / 60000),
          Math.floor((remaining % 60000) / 1000),
        ].map((value) => String(value).padStart(2, '0'));
        page.querySelectorAll('[data-ab-time]').forEach((node, index) => { node.textContent = values[index]; });
      };
      tick();
      window.setInterval(tick, 1000);
    }

    const galleryTrack = page.querySelector('[data-ab-gallery-track]');
    const gallerySlides = Array.from(page.querySelectorAll('[data-ab-gallery-slide]'));
    const galleryThumbs = Array.from(page.querySelectorAll('[data-ab-gallery-thumb]'));
    const galleryDots = Array.from(page.querySelectorAll('[data-ab-gallery-dot]'));
    if (galleryTrack && gallerySlides.length) {
      let activeGalleryIndex = 0;
      let galleryScrollFrame;
      let galleryDragging = false;
      let galleryMoved = false;
      let galleryStartX = 0;
      let galleryStartScroll = 0;

      const updateGallery = (nextIndex) => {
        const index = Math.max(0, Math.min(gallerySlides.length - 1, nextIndex));
        if (index !== activeGalleryIndex) {
          gallerySlides[activeGalleryIndex]?.querySelectorAll('video').forEach((video) => video.pause());
          activeGalleryIndex = index;
        }
        galleryThumbs.forEach((thumb, thumbIndex) => {
          const active = thumbIndex === index;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', active ? 'true' : 'false');
        });
        galleryDots.forEach((dot, dotIndex) => {
          const active = dotIndex === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
      };

      const showGalleryItem = (nextIndex, behavior = 'smooth') => {
        const index = Math.max(0, Math.min(gallerySlides.length - 1, nextIndex));
        galleryTrack.scrollTo({ left: index * galleryTrack.clientWidth, behavior });
        updateGallery(index);
      };

      galleryThumbs.forEach((thumb) => thumb.addEventListener('click', () => showGalleryItem(Number(thumb.dataset.index || 0))));
      galleryDots.forEach((dot) => dot.addEventListener('click', () => showGalleryItem(Number(dot.dataset.index || 0))));
      galleryTrack.addEventListener('scroll', () => {
        if (galleryScrollFrame) return;
        galleryScrollFrame = window.requestAnimationFrame(() => {
          const width = Math.max(1, galleryTrack.clientWidth);
          updateGallery(Math.round(galleryTrack.scrollLeft / width));
          galleryScrollFrame = null;
        });
      }, { passive: true });
      galleryTrack.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        showGalleryItem(activeGalleryIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });

      galleryTrack.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest?.('video, iframe, model-viewer, button, a, input')) return;
        galleryDragging = true;
        galleryMoved = false;
        galleryStartX = event.clientX;
        galleryStartScroll = galleryTrack.scrollLeft;
        galleryTrack.classList.add('is-dragging');
        galleryTrack.setPointerCapture(event.pointerId);
      });
      galleryTrack.addEventListener('pointermove', (event) => {
        if (!galleryDragging) return;
        const distance = event.clientX - galleryStartX;
        if (Math.abs(distance) > 4) galleryMoved = true;
        galleryTrack.scrollLeft = galleryStartScroll - distance;
        if (galleryMoved) event.preventDefault();
      });
      const finishGalleryDrag = (event) => {
        if (!galleryDragging) return;
        galleryDragging = false;
        galleryTrack.classList.remove('is-dragging');
        if (galleryTrack.hasPointerCapture(event.pointerId)) galleryTrack.releasePointerCapture(event.pointerId);
        showGalleryItem(Math.round(galleryTrack.scrollLeft / Math.max(1, galleryTrack.clientWidth)));
      };
      galleryTrack.addEventListener('pointerup', finishGalleryDrag);
      galleryTrack.addEventListener('pointercancel', finishGalleryDrag);
      galleryTrack.addEventListener('click', (event) => {
        if (!galleryMoved) return;
        event.preventDefault();
        event.stopPropagation();
        galleryMoved = false;
      }, true);
      window.addEventListener('resize', () => showGalleryItem(activeGalleryIndex, 'auto'));
      updateGallery(0);
    }

    const currency = page.dataset.currency || 'EUR';
    const bundles = Array.from(page.querySelectorAll('[data-ab-bundle]'));
    const form = page.querySelector('form[data-ab-product-form]');
    const submit = form?.querySelector('[type="submit"]');
    const idInput = form?.querySelector('[name="id"]');
    const quantityInput = form?.querySelector('[name="quantity"]');
    const offerInput = form?.querySelector('[name="properties[Angebot]"]');
    const bundleKeyInput = form?.querySelector('[data-ab-bundle-key-input]');
    const label = page.querySelector('[data-ab-cta-label]');
    const soldOut = page.querySelector('[data-ab-sold-out]');
    const stickyPrice = page.querySelector('[data-ab-sticky-price]');

    const syncInput = (input, value) => {
      if (!input) return;
      const nextValue = String(value ?? '');
      input.value = nextValue;
      input.setAttribute('value', nextValue);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const chooseBundle = (bundle) => {
      if (!bundle) return;
      bundles.forEach((item) => {
        const active = item === bundle;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      const available = bundle.dataset.available === 'true' && Boolean(bundle.dataset.variantId);
      syncInput(idInput, bundle.dataset.variantId || '');
      syncInput(quantityInput, bundle.dataset.quantity || '1');
      syncInput(offerInput, bundle.dataset.title || '');
      syncInput(bundleKeyInput, bundle.dataset.bundleKey || '');
      if (stickyPrice) stickyPrice.textContent = bundle.dataset.displayPrice || formatMoney(bundle.dataset.price, currency);
      if (submit) {
        submit.disabled = !available;
        submit.setAttribute('aria-disabled', available ? 'false' : 'true');
      }
      label?.classList.toggle('hidden', !available);
      soldOut?.classList.toggle('hidden', available);
    };

    bundles.forEach((bundle) => bundle.addEventListener('click', () => chooseBundle(bundle)));
    const preferred = page.querySelector('[data-ab-bundle].is-active');
    chooseBundle(preferred?.dataset.available === 'true' ? preferred : bundles.find((bundle) => bundle.dataset.available === 'true') || preferred || bundles[0]);

    const cartError = page.querySelector('[data-ab-cart-error]');
    const setCartError = (message = '') => {
      if (!cartError) return;
      cartError.textContent = message;
      cartError.hidden = !message;
    };

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;

      const drawer = document.querySelector('cart-drawer.aurevona-cart');
      const addUrl = window.routes?.cart_add_url;
      if (!drawer || !addUrl) {
        setCartError('Der Warenkorb konnte nicht geöffnet werden. Bitte lade die Seite neu und versuche es erneut.');
        return;
      }

      setCartError();
      submit.disabled = true;
      submit.setAttribute('aria-disabled', 'true');
      submit.setAttribute('aria-busy', 'true');
      submit.classList.add('is-loading');

      try {
        const formData = new FormData(form);
        const sections = typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map((section) => section.id)
          : ['cart-drawer', 'cart-icon-bubble'];
        const variantId = Number.parseInt(String(formData.get('id') || ''), 10);
        const quantity = Math.max(1, Number.parseInt(String(formData.get('quantity') || '1'), 10) || 1);
        const properties = {};
        formData.forEach((value, key) => {
          if (!key.startsWith('properties[') || !key.endsWith(']') || value === '') return;
          properties[key.slice(11, -1)] = String(value);
        });
        if (!variantId) throw new Error('Bitte wähle ein verfügbares Paket.');

        const items = [{ id: variantId, quantity, properties }];

        if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(submit);

        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();

        if (!response.ok || state.status || state.errors) {
          throw new Error(state.description || state.message || state.errors || 'Das Produkt konnte nicht hinzugefügt werden.');
        }
        if (!state.sections || typeof drawer.renderContents !== 'function') {
          throw new Error('Der Warenkorb konnte nicht aktualisiert werden. Bitte versuche es erneut.');
        }

        drawer.renderContents({ ...state, id: state.id || variantId });
      } catch (error) {
        setCartError(error.message || 'Das Produkt konnte nicht hinzugefügt werden. Bitte versuche es erneut.');
      } finally {
        submit.disabled = false;
        submit.setAttribute('aria-disabled', 'false');
        submit.removeAttribute('aria-busy');
        submit.classList.remove('is-loading');
      }
    });

    page.querySelectorAll('[data-ab-submit-proxy]').forEach((proxy) => proxy.addEventListener('click', () => {
      if (!submit || submit.disabled) {
        page.querySelector('.ab-bundles')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      form.requestSubmit(submit);
    }));

    const reviewCarousel = page.querySelector('.ab-review-grid');
    const reviewPrevious = page.querySelector('[data-ab-review-prev]');
    const reviewNext = page.querySelector('[data-ab-review-next]');
    if (reviewCarousel && reviewPrevious && reviewNext) {
      const getReviewStep = () => {
        const card = reviewCarousel.querySelector('.ab-review-card');
        if (!card) return reviewCarousel.clientWidth;
        const styles = window.getComputedStyle(reviewCarousel);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return card.getBoundingClientRect().width + gap;
      };

      const updateReviewControls = () => {
        const maximum = Math.max(0, reviewCarousel.scrollWidth - reviewCarousel.clientWidth);
        const scrollable = maximum > 4;
        reviewPrevious.disabled = !scrollable || reviewCarousel.scrollLeft <= 4;
        reviewNext.disabled = !scrollable || reviewCarousel.scrollLeft >= maximum - 4;
      };

      const scrollReviews = (direction) => {
        reviewCarousel.scrollBy({
          left: direction * getReviewStep(),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      };

      let reviewFrame;
      reviewPrevious.addEventListener('click', () => scrollReviews(-1));
      reviewNext.addEventListener('click', () => scrollReviews(1));
      reviewCarousel.addEventListener('scroll', () => {
        if (reviewFrame) return;
        reviewFrame = window.requestAnimationFrame(() => {
          updateReviewControls();
          reviewFrame = null;
        });
      }, { passive: true });
      window.addEventListener('resize', updateReviewControls);
      updateReviewControls();
    }

    const ingredientCarousel = page.querySelector('[data-ab-ingredient-carousel]');
    const ingredientPrevious = page.querySelector('[data-ab-ingredient-prev]');
    const ingredientNext = page.querySelector('[data-ab-ingredient-next]');
    if (ingredientCarousel && ingredientPrevious && ingredientNext) {
      const getIngredientStep = () => {
        const card = ingredientCarousel.querySelector('.ab-ingredient-card');
        if (!card) return ingredientCarousel.clientWidth;
        const styles = window.getComputedStyle(ingredientCarousel);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return card.getBoundingClientRect().width + gap;
      };

      const updateIngredientControls = () => {
        const maximum = Math.max(0, ingredientCarousel.scrollWidth - ingredientCarousel.clientWidth);
        const scrollable = maximum > 4;
        ingredientPrevious.disabled = !scrollable || ingredientCarousel.scrollLeft <= 4;
        ingredientNext.disabled = !scrollable || ingredientCarousel.scrollLeft >= maximum - 4;
      };

      const scrollIngredients = (direction) => ingredientCarousel.scrollBy({
        left: direction * getIngredientStep(),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });

      let ingredientFrame;
      ingredientPrevious.addEventListener('click', () => scrollIngredients(-1));
      ingredientNext.addEventListener('click', () => scrollIngredients(1));
      ingredientCarousel.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        scrollIngredients(event.key === 'ArrowRight' ? 1 : -1);
      });
      ingredientCarousel.addEventListener('scroll', () => {
        if (ingredientFrame) return;
        ingredientFrame = window.requestAnimationFrame(() => {
          updateIngredientControls();
          ingredientFrame = null;
        });
      }, { passive: true });
      window.addEventListener('resize', updateIngredientControls);
      updateIngredientControls();
    }
  };

  const initializeAll = (root = document) => root.querySelectorAll('[data-ab-page]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
